import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import {
  PaginationControls,
  usePaginatedRows,
  useTableControls,
} from '../components/TableControls';
import { adapter } from '../data/mockAdapter';
import type { CfModuleWhitelistEntry, CfModuleType } from '@ascend/mocked-shared/mock-data';

const TYPE_BADGE: Record<CfModuleType, string> = {
  Fee: 'bg-green-100 text-green-700',
  Liquidity: 'bg-blue-100 text-blue-700',
  Risk: 'bg-red-100 text-red-700',
  Valuation: 'bg-purple-100 text-purple-700',
  Auction: 'bg-yellow-100 text-yellow-700',
  Oracle: 'bg-orange-100 text-orange-700',
  Compliance: 'bg-indigo-100 text-indigo-700',
};

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

// ─── Modal ──────────────────────────────────────────────────────────────────

function Modal({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-50 w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Config Display Helper ──────────────────────────────────────────────────

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function FeeSettings() {
  return (
    <div className="space-y-1">
      <ConfigRow label="Management Fee" value="0.50% annual" />
      <ConfigRow label="Performance Fee" value="10.00%" />
      <ConfigRow label="Fee Recipient" value="0x742d...bEb0" />
      <ConfigRow label="Accrual Period" value="Daily" />
    </div>
  );
}

function AuctionSettings() {
  return (
    <div className="space-y-1">
      <ConfigRow label="Auction Duration" value="24 hours" />
      <ConfigRow label="Reserve Price Formula" value="Haircut Adjusted" />
      <ConfigRow label="Reserve Discount" value="500 bps (5%)" />
      <ConfigRow label="Min Bid Increment" value="100 bps (1%)" />
    </div>
  );
}

function RiskSettings() {
  return (
    <div className="space-y-1">
      <ConfigRow label="Min Health Factor" value="1.10" />
      <ConfigRow label="Liquidation Threshold" value="150%" />
      <ConfigRow label="Max LTV" value="67%" />
      <ConfigRow label="Grace Period" value="1 hour" />
    </div>
  );
}

function OracleSettings() {
  return (
    <div className="space-y-1">
      <ConfigRow label="Update Frequency" value="15 min" />
      <ConfigRow label="Staleness Threshold" value="60 min" />
      <ConfigRow label="Deviation Threshold" value="50 bps (0.5%)" />
      <ConfigRow label="Fallback Oracle" value="None" />
    </div>
  );
}

function ValuationSettings() {
  return (
    <div className="space-y-1">
      <ConfigRow label="Valuation Method" value="NAV-based" />
      <ConfigRow label="Update Frequency" value="Daily" />
      <ConfigRow label="Haircut" value="15%" />
    </div>
  );
}

function LiquiditySettings() {
  return (
    <div className="space-y-1">
      <ConfigRow label="Max Slippage" value="50 bps (0.5%)" />
      <ConfigRow label="Min Liquidity Depth" value="$100,000" />
      <ConfigRow label="Routing Strategy" value="Best Price" />
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

export function ModuleDetailPage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();

  const [mod, setMod] = useState<CfModuleWhitelistEntry | null>(null);
  const [facilities, setFacilities] = useState<Array<{ facilityId: string; facilityName: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Edit form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAddress, setFormAddress] = useState('');

  const { page, setPage, pageSize, setPageSize } = useTableControls();

  const refresh = useCallback(async () => {
    if (!moduleId) return;
    const [moduleData, facilitiesData] = await Promise.all([
      adapter.modules.byId(moduleId),
      adapter.modules.listFacilitiesByModule(moduleId),
    ]);
    setMod(moduleData);
    setFacilities(facilitiesData);
    if (moduleData) {
      setFormName(moduleData.name);
      setFormDescription(moduleData.description);
      setFormAddress(moduleData.chainAddress);
    }
    setLoading(false);
  }, [moduleId]);

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    (async () => {
      const [moduleData, facilitiesData] = await Promise.all([
        adapter.modules.byId(moduleId),
        adapter.modules.listFacilitiesByModule(moduleId),
      ]);
      if (cancelled) return;
      setMod(moduleData);
      setFacilities(facilitiesData);
      if (moduleData) {
        setFormName(moduleData.name);
        setFormDescription(moduleData.description);
        setFormAddress(moduleData.chainAddress);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  const { rows: paginatedFacilities } = usePaginatedRows(facilities, page, pageSize);

  async function handleSave() {
    if (!mod || !formName.trim() || !formAddress.trim()) return;
    await adapter.modules.update(mod.id, {
      name: formName.trim(),
      description: formDescription.trim(),
      chainAddress: formAddress.trim(),
    });
    toast.success('Module updated');
    setEditing(false);
    await refresh();
  }

  function handleCancelEdit() {
    if (mod) {
      setFormName(mod.name);
      setFormDescription(mod.description);
      setFormAddress(mod.chainAddress);
    }
    setEditing(false);
  }

  async function handleDelete() {
    if (!mod) return;
    await adapter.modules.delete(mod.id);
    toast.success(`Module "${mod.name}" removed`);
    navigate('/module-management');
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <p className="text-muted-foreground">Loading module...</p>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/module-management')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" /> Back to Module Whitelist
        </button>
        <p className="text-muted-foreground">Module not found.</p>
      </div>
    );
  }

  const badge = TYPE_BADGE[mod.moduleType];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/module-management')}
            className="mt-1.5 rounded-md p-1 hover:bg-accent transition-colors"
          >
            <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-5 w-5 text-foreground" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <Icon name="streamline:module-three" className="h-6 w-6 text-foreground" />
              <h1 className="text-xl sm:text-2xl font-bold">{mod.name}</h1>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${badge}`}>
                {mod.moduleType}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {mod.description || 'No description'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <>
              <Button variant="outline" onClick={() => setEditing(true)} className="flex items-center gap-2">
                <Icon name="streamline:interface-edit-pencil-change-edit-modify-pencil-write-writing" className="h-4 w-4" /> Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDelete(true)} className="flex items-center gap-2">
                <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Module Details Card */}
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-5">
        <h3 className="text-lg font-semibold">Module Configuration</h3>

        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Name</label>
              <input type="text" className={INPUT_CLASS} value={formName} onChange={(e) => setFormName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Description</label>
              <textarea className={INPUT_CLASS} rows={3} value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Chain Address</label>
              <input type="text" className={INPUT_CLASS} value={formAddress} onChange={(e) => setFormAddress(e.target.value)} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSave} className="flex items-center gap-2">
                <Icon name="streamline:interface-content-save-disk-floppy-electronics-device-disc-computer" className="h-4 w-4" /> Save Changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Module Type</p>
              <p className="text-sm">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>{mod.moduleType}</span>
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Chain ID</p>
              <p className="text-sm">{mod.chainId}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Chain Address</p>
              <p className="text-sm font-mono">{mod.chainAddress}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Description</p>
              <p className="text-sm">{mod.description || 'No description provided.'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Last Updated</p>
              <p className="text-sm">{new Date(mod.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        )}
      </div>

      {/* Type-specific configuration */}
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-4">
        <h3 className="text-lg font-semibold">{mod.moduleType} Settings</h3>
        {mod.moduleType === 'Fee' && <FeeSettings />}
        {mod.moduleType === 'Auction' && <AuctionSettings />}
        {mod.moduleType === 'Risk' && <RiskSettings />}
        {mod.moduleType === 'Oracle' && <OracleSettings />}
        {mod.moduleType === 'Valuation' && <ValuationSettings />}
        {mod.moduleType === 'Liquidity' && <LiquiditySettings />}
      </div>

      {/* Credit Facilities Using This Module */}
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Credit Facilities</h3>
          <span className="text-sm text-muted-foreground">{facilities.length} facilit{facilities.length === 1 ? 'y' : 'ies'} using this module</span>
        </div>

        {facilities.length === 0 ? (
          <p className="text-sm text-muted-foreground">No credit facilities are currently using this module.</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Facility Name</th>
                    <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">ID</th>
                    <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground w-12" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedFacilities.map((f) => (
                    <tr
                      key={f.facilityId}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/cf/${encodeURIComponent(f.facilityName)}`)}
                    >
                      <td className="px-3 py-2.5 font-medium">{f.facilityName}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{f.facilityId}</td>
                      <td className="px-3 py-2.5">
                        <Icon name="streamline:share-link" className="h-3.5 w-3.5 text-muted-foreground" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalItems={facilities.length} label="facilities" />
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal open={showDelete} onClose={() => setShowDelete(false)} title="Confirm Deletion">
        <div className="space-y-4">
          <p className="text-sm">
            Are you sure you want to remove <span className="font-semibold">{mod.name}</span> from the module whitelist? This will also remove it from any assigned credit facilities.
          </p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button variant="destructive" className="flex-1" onClick={handleDelete}>Delete Module</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
